import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Building, 
  Shield, 
  Cpu, 
  Wallet,
  Users,
  Globe,
  Target,
  Zap,
  Heart,
  Database,
  Scale,
  ClipboardList,
  Settings,
  CheckCircle,
  BarChart3,
  Layers,
  Calendar
} from 'lucide-react';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut' }
  }
};

const servicePillars = [
  {
    id: 'policy',
    icon: Building,
    number: '01',
    title: 'Policy Innovation & Strategic Governance',
    subtitle: 'Architecting National Health Productivity',
    description: 'We move beyond traditional administrative planning to treat health as a foundational pillar of national economic security.',
    features: [
      {
        icon: Zap,
        title: 'Systemic Leapfrogging in Policy',
        text: 'Design national and sub-national health plans that bypass linear developmental stages.'
      },
      {
        icon: Target,
        title: 'Governance Audits',
        text: 'Evaluating current policy frameworks to identify "Default" bottlenecks.'
      },
      {
        icon: Globe,
        title: 'Outcome-Driven Strategy',
        text: 'Crafting policies measured by long-term human capital development.'
      }
    ]
  },
  {
    id: 'security',
    icon: Shield,
    number: '02',
    title: 'Global Health Security & Nutrition',
    subtitle: 'Building Proactive "Frontier Defense" Systems',
    description: 'We specialize in biosecurity and nutritional integrity as systemic risk management.',
    features: [
      {
        icon: Shield,
        title: 'Biosecurity Frameworks',
        text: 'Designing "Frontier Defense" systems for proactive epidemic readiness.'
      },
      {
        icon: Heart,
        title: 'Nutritional Resilience',
        text: 'Treating nutrition as "biological infrastructure" for national productivity.'
      },
      {
        icon: Database,
        title: 'One Health Integration',
        text: 'Synchronizing human, animal, and environmental health data.'
      }
    ]
  },
  {
    id: 'technology',
    icon: Cpu,
    number: '03',
    title: 'Health Technology & AI Integration',
    subtitle: 'Digital Equity and High-Utility Intelligence',
    description: 'The future of health is digital, but only if it is integrated into the human workflow.',
    features: [
      {
        icon: Zap,
        title: 'AI for Systemic Leapfrogging',
        text: 'Implementing AI-driven diagnostics that work in low-resource settings.'
      },
      {
        icon: Database,
        title: 'Digital Ecosystem Architecture',
        text: 'Architecting ecosystems that ensure health technology scales equitably.'
      },
      {
        icon: Scale,
        title: 'Ethics & Digital Equity',
        text: 'Ensuring AI deployment reduces rather than widens the digital divide.'
      }
    ]
  },
  {
    id: 'financing',
    icon: Wallet,
    number: '04',
    title: 'Health Financing Advisory',
    subtitle: 'Designing Long-Term Fiscal Resilience',
    description: 'Sustainability is the hallmark of a designed system. We help clients move toward innovative, self-sustaining models.',
    features: [
      {
        icon: Target,
        title: 'Performance-Based Financing',
        text: 'Designing incentive structures that reward efficiency and actual health gains.'
      },
      {
        icon: Building,
        title: 'Public-Private Partnerships',
        text: 'Architecting collaborative models leveraging private sector efficiency.'
      },
      {
        icon: Zap,
        title: 'Marginal Utility Optimization',
        text: 'Auditing finance flows for high-yield, transformative interventions.'
      }
    ]
  },
  {
    id: 'leadership',
    icon: Users,
    number: '05',
    title: 'Strategic Health Leadership Development',
    subtitle: 'Strengthening the Human Architecture',
    description: 'Our deep expertise in leadership ensures that technical innovations are sustained by visionary governance.',
    features: [
      {
        icon: Users,
        title: 'Executive Capacity Building',
        text: 'High-level mentorship for health ministers and agency heads.'
      },
      {
        icon: Target,
        title: 'Change Management',
        text: 'Navigating the friction of systemic transitions with evidence-based leadership.'
      },
      {
        icon: Globe,
        title: 'The HSL Leadership Academy',
        text: 'Training a new generation of leaders for Leadership by Design.'
      }
    ]
  },
  {
    id: 'planning',
    icon: ClipboardList,
    number: '06',
    title: 'Health Service Planning',
    subtitle: 'Strategic Blueprints for Health System Excellence',
    description: 'We design comprehensive health service delivery plans that align resources, infrastructure, and human capital with population health needs.',
    features: [
      {
        icon: Target,
        title: 'Needs Assessment & Gap Analysis',
        text: 'Systematic evaluation of population health needs and current service capacity gaps.'
      },
      {
        icon: Layers,
        title: 'Service Delivery Mapping',
        text: 'Strategic mapping of healthcare facilities, referral pathways, and resource allocation.'
      },
      {
        icon: Calendar,
        title: 'Phased Implementation Roadmaps',
        text: 'Developing actionable timelines with measurable milestones for health service expansion.'
      }
    ]
  },
  {
    id: 'implementation',
    icon: Settings,
    number: '07',
    title: 'Implementation Management',
    subtitle: 'From Strategy to Sustainable Impact',
    description: 'We bridge the gap between planning and execution, ensuring health initiatives are delivered on time, within budget, and to specification.',
    features: [
      {
        icon: CheckCircle,
        title: 'Project Execution & Oversight',
        text: 'End-to-end management of health program rollouts with rigorous quality control.'
      },
      {
        icon: BarChart3,
        title: 'Performance Monitoring',
        text: 'Real-time tracking of implementation progress against defined KPIs and targets.'
      },
      {
        icon: Users,
        title: 'Stakeholder Coordination',
        text: 'Facilitating collaboration between government, donors, and implementing partners.'
      }
    ]
  }
];

export const ServicePillarsPage = () => {
  return (
    <div data-testid="service-pillars-page">
      {/* Hero Section */}
      <section className="section-padding pt-32" data-testid="services-hero">
        <div className="container-main">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="max-w-4xl"
          >
            <motion.span 
              variants={itemVariants}
              className="font-mono text-xs uppercase tracking-widest text-teal-600 mb-6 block"
            >
              Service Pillars
            </motion.span>
            
            <motion.h1 
              variants={itemVariants}
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.1] mb-6"
            >
              Integrated Solutions for Health & Social Development
            </motion.h1>
            
            <motion.p 
              variants={itemVariants}
              className="text-lg md:text-xl text-slate-600 leading-relaxed"
            >
              At HSL, we do not view health and social development sectors in isolation. Our <span className="text-teal-600 font-semibold">Ambidextrous 
              Strategy</span> allows us to address immediate sectoral gaps while simultaneously architecting 
              long-term resilience. We provide deep technical expertise across seven integrated health service pillars, 
              unified by the HSL Impact Model.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Service Pillars */}
      <section className="section-padding bg-slate-50" data-testid="pillars-grid">
        <div className="container-main">
          <div className="space-y-8">
            {servicePillars.map((pillar, index) => (
              <motion.div
                key={pillar.id}
                id={pillar.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="bg-white border border-slate-200 overflow-hidden hover:border-slate-300 transition-colors group"
                data-testid={`service-pillar-${pillar.id}`}
              >
                <div className="grid lg:grid-cols-12">
                  {/* Left Column - Header */}
                  <div className="lg:col-span-4 bg-slate-100/20 p-8 lg:p-10 border-b lg:border-b-0 lg:border-r border-slate-200">
                    <div className="flex items-start gap-4 mb-6">
                      <span className="font-heading text-5xl font-bold text-slate-700 group-hover:text-slate-600 transition-colors">
                        {pillar.number}
                      </span>
                      <div className="w-12 h-12 bg-teal-100 border border-teal-200 flex items-center justify-center">
                        <pillar.icon size={24} className="text-teal-600" />
                      </div>
                    </div>
                    
                    <h3 className="font-heading text-2xl text-slate-900 mb-2">{pillar.title}</h3>
                    <span className="text-teal-600 text-sm block mb-4">{pillar.subtitle}</span>
                    <p className="text-slate-500 leading-relaxed">{pillar.description}</p>
                  </div>

                  {/* Right Column - Features */}
                  <div className="lg:col-span-8 p-8 lg:p-10">
                    <div className="grid md:grid-cols-3 gap-6">
                      {pillar.features.map((feature, i) => (
                        <div 
                          key={i} 
                          className="bg-slate-100/20 p-5 hover:bg-slate-100/40 transition-colors"
                        >
                          <div className="w-10 h-10 bg-slate-100 flex items-center justify-center mb-4">
                            <feature.icon size={20} className="text-teal-600" />
                          </div>
                          <h4 className="text-slate-900 font-medium mb-2 text-sm">{feature.title}</h4>
                          <p className="text-slate-500 text-sm leading-relaxed">{feature.text}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Connection Section */}
      <section className="section-padding" data-testid="connection-section">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <span className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-4 block">
              Unified Approach
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
              Deep Local Context, Global Standards
            </h2>
            <p className="text-slate-600 text-lg">
              All seven service pillars are connected through the HSL Impact Model, ensuring that interventions 
              in one area reinforce and amplify outcomes across the entire health system.
            </p>
          </motion.div>

          {/* Visual Connection */}
          <div className="relative">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
              {servicePillars.map((pillar, index) => (
                <motion.div
                  key={pillar.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  className="bg-slate-50 border border-slate-200 p-6 text-center hover:border-teal-500 transition-colors group"
                >
                  <div className="w-12 h-12 bg-slate-100 flex items-center justify-center mx-auto mb-3 group-hover:bg-teal-200 transition-colors">
                    <pillar.icon size={24} className="text-slate-500 group-hover:text-teal-600 transition-colors" />
                  </div>
                  <span className="text-slate-900 text-sm font-medium block">
                    {pillar.title.split(' ').slice(0, 2).join(' ')}
                  </span>
                </motion.div>
              ))}
            </div>
            
            {/* Center Connection */}
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 hidden md:flex"
            >
              <div className="w-24 h-24 bg-teal-500/20 border border-teal-500/50 rounded-full flex items-center justify-center">
                <span className="font-heading text-teal-600 text-sm text-center leading-tight">
                  HSL<br/>Impact<br/>Model
                </span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-slate-50" data-testid="services-cta">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative overflow-hidden bg-white border border-slate-200 p-12 md:p-16 text-center"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-teal-500/5 via-transparent to-teal-500/5" />
            
            <div className="relative">
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
                Ready to Transform Your Health System?
              </h2>
              <p className="text-slate-500 text-lg mb-8 max-w-2xl mx-auto">
                Discover how our integrated service pillars can address your specific challenges.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contact"
                  className="btn-primary inline-flex items-center justify-center gap-2 group"
                  data-testid="services-cta-primary"
                >
                  Schedule a Consultation
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/methodology"
                  className="btn-secondary inline-flex items-center justify-center gap-2"
                  data-testid="services-cta-secondary"
                >
                  Learn Our Methodology
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
